// @flow
import * as React from 'react'
import { useContext, useRef } from 'react'
import ReactDOM from 'react-dom'

import { useKeyPress } from '~/core/utils/hooks'

type ModalContextType = {
  close: () => void,
  registerCloseListener: (callback: () => any) => void,
  unregisterCloseListener: () => void
}

const ModalContext = React.createContext<ModalContextType>({})

type ModalProps = {
  title: string,
  children: React.Node,
  isCloseDisabled?: boolean,
  isOpen?: boolean,
  onClose?: () => void
}

const getModalRoot = () => {
  const fullscreenElement = document.fullscreenElement
  // Attach to actual fullscreen element to ensure modal visibility
  if (fullscreenElement !== null) {
    return fullscreenElement
  }
  return document.getElementById('modal-root')
}

const Modal = ({ children, isCloseDisabled, isOpen, title, onClose = () => {}, ...props }: ModalProps) => {
  const closeCallBackRef = useRef(null)
  const modalRoot = getModalRoot()

  const handleClose = (closeCallback?: (() => void) | null) => {
    if (!isCloseDisabled) {
      onClose()
      if (typeof closeCallback === 'function') {
        closeCallback()
      }
    }
  }

  const registerCloseListener = callback => {
    closeCallBackRef.current = callback
  }

  const unregisterCloseListener = () => {
    closeCallBackRef.current = null
  }

  useKeyPress('Escape', () => handleClose.call(null, closeCallBackRef.current), isOpen, [isCloseDisabled])

  return isOpen && modalRoot !== null
    ? ReactDOM.createPortal(
        <ModalContext.Provider
          value={{
            close: () => handleClose(closeCallBackRef.current),
            registerCloseListener,
            unregisterCloseListener
          }}
        >
          <div
            className="modal-overlay"
            style={{ display: 'flex', justifyContent: 'center', zIndex: 99999, overflow: 'auto' }}
          >
            <article
              className="modal"
              style={{
                display: 'flex',
                maxHeight: '80%',
                width: 'auto',
                left: 'auto',
                right: 'auto'
              }}
              {...props}
            >
              <header
                className="modal_header"
                style={{
                  justifyContent: isCloseDisabled ? 'flex-end' : 'space-between'
                }}
              >
                {!isCloseDisabled && (
                  <a className="btn btn-flat modal_close">
                    <i className="icon icon-m" onClick={() => handleClose(closeCallBackRef.current)}>
                      close
                    </i>
                  </a>
                )}
                <h2 className="text-color-theme text-upper" style={{ fontSize: '1.2rem', textTransform: 'none' }}>
                  {title}
                </h2>
              </header>
              <section className="modal_content" style={{ overflow: 'visible' }}>
                {children}
              </section>
            </article>
          </div>
        </ModalContext.Provider>,
        modalRoot
      )
    : null
}

const useModalContext = () => {
  const context = useContext(ModalContext)

  if (!context) {
    throw new Error('[ModalContext] useModalContext must be used within ModalContext provider')
  }

  return context
}

export { useModalContext, Modal }
